/**
 * How many calls are still owed a call back, and the one act that changes it.
 *
 * Its own hook rather than four more `useState`s in `CallsSection`, following
 * `useCallDivert` next door: that file exists because the rail needs a settings
 * row it does not own, and this one because the rail needs a number it does not
 * own. `CallsSection` is already past four hundred lines and holds every
 * callback on the desk; a queue that is read in one place, written in another
 * and drawn in a third is exactly the shape that should leave it.
 *
 * The count is DESK-WIDE and does not clear on looking - see the API's
 * `lib/callback.ts`. That is the difference between it and the bell beside it,
 * and it is why the number has to come from the server rather than be counted
 * off the loaded page: the middle pane holds a hundred rows, and a work list is
 * about everything left, not about what fits.
 */
import { useCallback, useEffect, useState } from "react";
import { useI18n } from "../../../i18n/I18nProvider";
import { getCallbackCount, setCallHandled } from "../../../lib/callback";
import { useToast } from "../../ui";

export interface CallbackQueueApi {
  /** Still waiting, desk-wide. Zero until the first read lands. */
  count: number;
  /** Tick one off, or put it back. Resolves to the row's new `handledAt`, or
   *  `undefined` when the write was refused - the caller uses that to decide
   *  whether to repaint the list. */
  setHandled: (id: string, handled: boolean) => Promise<string | null | undefined>;
  /** Re-read the number. Called after anything that could have closed a call
   *  without going through `setHandled` - an outgoing call, most of all. */
  refresh: () => void;
}

/**
 * @param allowed This operator may work the telephone at all. False keeps the
 *   hook inert rather than asking for a count the server would refuse.
 */
export function useCallbackQueue(allowed: boolean): CallbackQueueApi {
  const { t } = useI18n();
  const toast = useToast();
  const [count, setCount] = useState(0);

  const refresh = useCallback(() => {
    if (!allowed) return;
    // Silently on failure. A rail badge that cannot be read is a missing number,
    // and the folder underneath it still opens and still lists the work.
    getCallbackCount()
      .then((r) => setCount(r.count))
      .catch(() => {});
  }, [allowed]);

  useEffect(refresh, [refresh]);

  const setHandled = useCallback(
    async (id: string, handled: boolean) => {
      try {
        const res = await setCallHandled(id, handled);
        // The fresh count rides back on the write, so the badge moves on the
        // same round trip that moved the row rather than a request later.
        setCount(res.count);
        toast(t(handled ? "calls.callback.toastDone" : "calls.callback.toastUndone"));
        return res.call.handledAt;
      } catch {
        toast(t("common.error"));
        return undefined;
      }
    },
    [t, toast],
  );

  return { count, setHandled, refresh };
}
